import React, { useState, useEffect } from 'react';
import { signIn, signOut, getCurrentUser } from 'aws-amplify/auth';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { toast } from 'sonner';
import { APP_VERSION } from '../../version';
import { accessControlService } from '../../services/accessControlService';
import { UserPlus, AlertTriangle } from 'lucide-react';

interface InviteInfo {
  invite_id: string;
  org_id: string;
  org_name: string;
  invitee_email: string;
  role: string;
}

export const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showSignOutOption, setShowSignOutOption] = useState(false);
  const [inviteInfo, setInviteInfo] = useState<InviteInfo | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [isLoadingInvite, setIsLoadingInvite] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const checkExistingSession = async () => {
    try {
      const user = await getCurrentUser();
      if (user) {
        // User is already authenticated, redirect to dashboard
        // console.log('User already authenticated, redirecting to dashboard');
        toast.success('Welcome back! Redirecting to dashboard...');
        navigate('/dashboard');
        return;
      }
    } catch (error) {
      // No existing session, continue normally
      // console.log('No existing session found');
      setShowSignOutOption(false);
    }
  };

  useEffect(() => {
    checkExistingSession();
  }, [navigate]);

  // Check for invite parameter in URL
  useEffect(() => {
    const inviteId = searchParams.get('invite');
    if (inviteId) {
      loadInviteDetails(inviteId);
    }
  }, [searchParams]);

  const loadInviteDetails = async (inviteId: string) => {
    setIsLoadingInvite(true);
    setInviteError(null);

    try {
      const result = await accessControlService.lookupInvite(inviteId);

      if (result.success && result.org_name) {
        setInviteInfo({
          invite_id: result.invite_id!,
          org_id: result.org_id!,
          org_name: result.org_name,
          invitee_email: result.invitee_email || '',
          role: result.role || 'member',
        });
        // Pre-fill email if provided
        if (result.invitee_email) {
          setEmail(result.invitee_email);
        }
      } else {
        setInviteError(result.error || 'Invalid or expired invite');
      }
    } catch (error) {
      console.error('Error loading invite:', error);
      setInviteError('Failed to load invite details');
    } finally {
      setIsLoadingInvite(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      setShowSignOutOption(false);
      toast.success('Signed out successfully. You can now sign in with a different account.');
    } catch (error) {
      // console.error('Sign out error:', error);
      toast.error('Failed to sign out. Please try again.');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await signIn({
        username: email,
        password,
      });

      // If there's an invite, accept it after successful sign-in
      if (inviteInfo) {
        try {
          // Validate email matches invite (if specified)
          if (inviteInfo.invitee_email && email.toLowerCase() !== inviteInfo.invitee_email.toLowerCase()) {
            toast.error(`This invite was sent to ${inviteInfo.invitee_email}. Please sign in with that email.`);
            setIsLoading(false);
            return;
          }

          const acceptResult = await accessControlService.acceptInvite(
            inviteInfo.org_id,
            inviteInfo.invite_id
          );

          if (acceptResult.success) {
            toast.success(`Welcome to ${inviteInfo.org_name}!`);
          } else {
            toast.error(`Could not accept invite: ${acceptResult.error}`);
          }
        } catch (acceptError) {
          console.error('Failed to accept invite:', acceptError);
          toast.error('Failed to accept invite. Please try again from the dashboard.');
        }
      } else {
        toast.success('Login successful!');
      }

      navigate('/dashboard');
    } catch (error: any) {
      // console.error('Login error:', error);
      
      // Handle specific error cases
      if (error.message && error.message.includes('already a signed in user')) {
        // Instead of showing sign out option, try to redirect to dashboard
        // console.log('Already signed in error, checking current user...');
        try {
          const user = await getCurrentUser();
          if (user) {
            toast.success('You are already signed in! Redirecting to dashboard...');
            navigate('/dashboard');
            return;
          }
        } catch (checkError) {
          // If we can't get current user, show sign out option as fallback
          setShowSignOutOption(true);
          toast.error('There is already a signed in user. Please sign out first to continue.');
        }
      } else {
        toast.error(error.message || 'Login failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4 sm:px-6 lg:px-8">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <div className="flex items-baseline justify-center gap-2">
            <CardTitle className="text-3xl font-bold text-center font-mono">BigSplice</CardTitle>
            <span className="text-sm text-muted-foreground font-mono">v{APP_VERSION}</span>
          </div>
          <CardDescription className="text-center">
            {inviteInfo ? 'Create an account or sign in to accept your invitation' : 'Sign in to your account to continue'}
          </CardDescription>

          {/* Invite Banner */}
          {isLoadingInvite && (
            <div className="text-center text-sm text-muted-foreground py-2">
              Loading invitation details...
            </div>
          )}

          {inviteInfo && (
            <Alert className="mt-4 border-primary/50 bg-primary/5">
              <UserPlus className="h-4 w-4 text-primary" />
              <AlertDescription className="ml-2">
                <span className="font-medium">You've been invited to join </span>
                <span className="font-bold text-primary">{inviteInfo.org_name}</span>
                <span className="font-medium"> as a </span>
                <span className="font-semibold">{inviteInfo.role}</span>
              </AlertDescription>
            </Alert>
          )}

          {inviteError && (
            <Alert variant="destructive" className="mt-4">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription className="ml-2">
                {inviteError}
              </AlertDescription>
            </Alert>
          )}
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isLoading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isLoading}
              />
            </div>
          </CardContent>
          <CardFooter className="flex flex-col space-y-4 pt-6 pb-6">
            {showSignOutOption && (
              <div className="w-full p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <p className="text-sm text-yellow-800 mb-2">
                  You are already signed in. Sign out to use a different account.
                </p>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={handleSignOut}
                >
                  Sign Out Current User
                </Button>
              </div>
            )}
            <Button
              type="submit"
              className="w-full"
              disabled={isLoading || showSignOutOption}
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </Button>
            <div className="text-sm text-center space-y-2">
              <Link
                to="/forgot-password"
                className="text-primary hover:underline"
              >
                Forgot your password?
              </Link>
              {inviteInfo && (
              <div>
                <Link
                  to={`/register?invite=${inviteInfo.invite_id}`}
                  className="text-primary hover:underline font-semibold"
                >
                  Create Account to Accept Invite
                </Link>
              </div>
              )}
            </div>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}; 